

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { tenantBlockedStore } from "@/lib/auth/tenantBlockedStore";
import { getCompany } from "@/lib/api/handlers/CompanyHandling";
import { getEventEntitlements } from "@/lib/api/clients/EventClient";
import { getCookie } from "@/lib/auth/cookies";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";

interface TenantBlockedContextValue {
  blocked: boolean;
  dismissedThisSession: boolean;
  setBlocked: (b: boolean) => void;
  dismiss: () => void;
  /** Remove the state + the backing cookie. Called when the backend reports the
   * block has been lifted (dispute closed = won). */
  clear: () => void;
}

const TenantBlockedContext = createContext<TenantBlockedContextValue | null>(null);

/**
 * F17 v5 context. Initial state is seeded in both shells (`DashboardShell`,
 * `EventLayoutShell`) by reading the {@code veltric_tenant_blocked} cookie
 * that ApiClient writes on any 403 + errorCode=tenantBlocked response
 * (PB-TBB-1 — the cockpit writes it; the backend never did).
 *
 * Two more ways in and one way out, so the strip tracks the block without a
 * reload:
 *  - `veltric:tenant-blocked`, dispatched by ApiClient with the cookie, flips
 *    every mounted provider to blocked — a write refused in THIS session shows
 *    the strip at once;
 *  - a provider that mounts MARKED asks the company whether the block still
 *    stands and clears the marker unless the read says `tenantBlocked: true`
 *    — a company that was never blocked has no such key at all, so on a
 *    successful read only `true` keeps the strip. A failed or a rejected read
 *    keeps it: unmarking on "don't know" is how a blocked tenant loses the one
 *    notice that explains why every save fails. A user with no own company id
 *    has no company that could be blocked — the marker came from a foreign
 *    company's event — and is unmarked without a read;
 *  - in the EVENT shell (`eventId` set) the company that OWNS the event
 *    decides what is SHOWN there — every save on its pages is judged against
 *    that company — but its answer never deletes the marker unless that
 *    company IS the user's own: the marker may be a still-true record of the
 *    home company's block, and a healthy foreign event says nothing about it.
 *
 * Client code that catches TenantBlockedError can still call
 * {@link setBlocked}(true) by hand.
 */
export function TenantBlockedProvider({
  children,
  initialBlocked,
  eventId = null,
}: {
  children: React.ReactNode;
  initialBlocked: boolean;
  /**
   * The event in scope (the event shell). The block that matters there is the
   * one on the company that OWNS the event — every save on its pages is
   * judged against that company — so the re-check asks it what to SHOW. The
   * stored marker is only cleared on that answer when the owning company is
   * the user's own; otherwise it stays for the next non-event mount to judge.
   */
  eventId?: string | null;
}) {
  const [blocked, setBlockedState] = useState(initialBlocked);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const setBlocked = useCallback((b: boolean) => {
    setBlockedState(b);
    if (b) setDismissedThisSession(false); // reset dismissal when freshly blocked
  }, []);

  const dismiss = useCallback(() => {
    setDismissedThisSession(true);
  }, []);

  const clear = useCallback(() => {
    setBlockedState(false);
    setDismissedThisSession(false);
    tenantBlockedStore.clear();
  }, []);

  useEffect(() => {
    const onBlocked = () => setBlocked(true);
    window.addEventListener("veltric:tenant-blocked", onBlocked);
    return () => {
      window.removeEventListener("veltric:tenant-blocked", onBlocked);
    };
  }, [setBlocked]);

  // Mount re-check, only when marked: the marker is a session cookie written
  // at the moment of a refusal, and nothing else tells the cockpit when the
  // block is lifted. Known limit: a 403 from a FOREIGN blocked company's
  // event marks this session too, until the next mount asks the own company.
  useEffect(() => {
    if (!initialBlocked) return;
    let cancelled = false;
    (async () => {
      try {
        if (eventId) {
          // Event scope: ask the event's OWNING company. `company` is null for
          // an orphan event — nothing there to be blocked.
          const entitlements = await getEventEntitlements(eventId);
          // An empty body is "don't know" — keep the marker, like a failed read.
          if (cancelled || entitlements == null) return;
          if (entitlements.company?.tenantBlocked === true) {
            // Also the way back in when the previous event hid the strip and
            // the shell moved to a blocked one without remounting.
            setBlockedState(true);
            return;
          }
          // Not blocked HERE: no strip on this event. The marker is another
          // matter — it records that the user's HOME company refused a write,
          // and an earlier version cleared it on this answer: one visit to a
          // healthy foreign event deleted a still-true marker and the strip
          // was gone from the dashboard too. Only the home company's own
          // answer may delete it — which this is exactly when the event's
          // company IS the home company.
          const homeCompanyId = await getCookie("companyId");
          if (cancelled) return;
          if (homeCompanyId && entitlements.company?.companyId === homeCompanyId) {
            clear();
            return;
          }
          setBlockedState(false);
          return;
        }
        // getCompany reads the same id and can only fail without one, which
        // would keep a foreign company's marker on every page until logout.
        if (!(await getCookie("companyId"))) {
          if (!cancelled) clear();
          return;
        }
        const company = await getCompany("tenantBlocked");
        if (cancelled || isServerErr(company)) return;
        // Only `true` keeps it, as the server's own gate reads the field
        // (`Boolean.TRUE.equals`). A company that was never blocked has no
        // such key at all — JSON-B drops the null — and an older backend
        // whose allowlist lacks the field fails the read rather than
        // omitting it, so on a successful read "absent" means "not blocked".
        if (company.tenantBlocked !== true) clear();
      } catch {
        // CompanyHandling rethrows on a refused read, the entitlements client
        // throws on any failure — keep the marker.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialBlocked, clear, eventId]);

  const value = useMemo(
    () => ({ blocked, dismissedThisSession, setBlocked, dismiss, clear }),
    [blocked, dismissedThisSession, setBlocked, dismiss, clear],
  );

  return (
    <TenantBlockedContext.Provider value={value}>
      {children}
    </TenantBlockedContext.Provider>
  );
}

export function useTenantBlocked(): TenantBlockedContextValue {
  const ctx = useContext(TenantBlockedContext);
  if (!ctx) {
    throw new Error("useTenantBlocked must be used within a TenantBlockedProvider");
  }
  return ctx;
}
